// ─── LocationService — Simple, reliable GPS tracking via polling ─────────────
// Uses getCurrentPosition on a 2-second interval instead of watchPosition,
// which stalls on many Android web browsers. No aggressive filtering —
// accepts every fix the OS gives us and lets RouteProcessor handle smoothing.
import type { GpsPoint, GpsMode } from "./types";
import { Capacitor } from "@capacitor/core";
import { Geolocation } from "@capacitor/geolocation";

export type GpsErrorType = "permission-denied" | "timeout" | "unavailable" | "not-supported";

export interface GpsDiagnostics {
  status: "idle" | "searching" | "acquired" | "lost" | "error";
  permission: "unknown" | "granted" | "denied" | "prompt";
  errorCode: number | null;
  errorMessage: string | null;
  latitude: number | null;
  longitude: number | null;
  accuracy: number | null;
  speed: number | null;
  heading: number | null;
  altitude: number | null;
  lastUpdate: number | null;
  platform: string;
  pointCount: number;
  fixValidated: boolean;
  gnssInfo: string;
}

type PointCallback = (point: GpsPoint | null) => void;
type ErrorCallback = (err: GpsErrorType) => void;
type DiagCallback = (diag: GpsDiagnostics) => void;

const POLL_INTERVAL_MS = 2000;
const FIX_TIMEOUT_MS = 30000;

export class LocationService {
  private pollTimer: ReturnType<typeof setInterval> | null = null;
  private mode: GpsMode = "high";
  private callback: PointCallback | null = null;
  private errorCallback: ErrorCallback | null = null;
  private diagCallback: DiagCallback | null = null;
  private running = false;
  private native = false;
  private pointCount = 0;
  private fetching = false;

  private diag: GpsDiagnostics = {
    status: "idle",
    permission: "unknown",
    errorCode: null,
    errorMessage: null,
    latitude: null,
    longitude: null,
    accuracy: null,
    speed: null,
    heading: null,
    altitude: null,
    lastUpdate: null,
    platform: this.detectPlatform(),
    pointCount: 0,
    fixValidated: false,
    gnssInfo: "A-GPS + Multi-GNSS (GPS/Galileo/GLONASS/BeiDou)",
  };

  private detectPlatform(): string {
    if (Capacitor.isNativePlatform()) return "native";
    const ua = navigator.userAgent;
    if (/iPhone|iPad|iPod/i.test(ua)) return "ios-web";
    if (/Android/i.test(ua)) return "android-web";
    return "desktop-web";
  }

  setMode(mode: GpsMode) {
    this.mode = mode;
  }

  onDiagnostics(cb: DiagCallback) {
    this.diagCallback = cb;
    this.emitDiag();
  }

  getDiagnostics(): GpsDiagnostics {
    return { ...this.diag };
  }

  start(callback: PointCallback, errorCallback?: ErrorCallback) {
    this.callback = callback;
    this.errorCallback = errorCallback ?? null;
    this.running = true;
    this.pointCount = 0;
    this.native = Capacitor.isNativePlatform();
    this.diag.status = "searching";
    this.diag.pointCount = 0;
    this.diag.fixValidated = false;
    this.diag.errorCode = null;
    this.diag.errorMessage = null;
    this.emitDiag();

    // Do first fetch immediately, then poll
    this.fetchPosition();
    this.pollTimer = setInterval(() => {
      if (!this.running || this.fetching) return;
      this.fetchPosition();
    }, POLL_INTERVAL_MS);
  }

  stop() {
    this.running = false;
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
    this.diag.status = "idle";
    this.diag.fixValidated = false;
    this.emitDiag();
  }

  private async fetchPosition() {
    if (this.fetching || !this.running) return;
    this.fetching = true;

    try {
      if (this.native) {
        await this.fetchNative();
      } else {
        await this.fetchWeb();
      }
    } catch {
      // Silent — next poll will retry
    } finally {
      this.fetching = false;
    }
  }

  private async fetchNative() {
    try {
      const pos = await Geolocation.getCurrentPosition({
        enableHighAccuracy: this.mode === "high",
        timeout: FIX_TIMEOUT_MS,
        maximumAge: 0,
      });
      this.handlePosition(
        pos.coords.latitude,
        pos.coords.longitude,
        pos.coords.accuracy,
        pos.coords.speed,
        pos.coords.heading,
        pos.coords.altitude,
        pos.timestamp ?? Date.now(),
      );
    } catch (err: unknown) {
      this.handleError(err);
    }
  }

  private fetchWeb(): Promise<void> {
    return new Promise((resolve) => {
      if (!navigator.geolocation) {
        this.diag.status = "error";
        this.diag.errorCode = -1;
        this.diag.errorMessage = "Geolocation API not supported";
        this.emitDiag();
        if (this.errorCallback) this.errorCallback("not-supported");
        resolve();
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (pos) => {
          this.handlePosition(
            pos.coords.latitude,
            pos.coords.longitude,
            pos.coords.accuracy,
            pos.coords.speed,
            pos.coords.heading,
            pos.coords.altitude,
            pos.timestamp,
          );
          resolve();
        },
        (err) => {
          this.handleWebError(err);
          resolve();
        },
        {
          enableHighAccuracy: this.mode === "high",
          timeout: FIX_TIMEOUT_MS,
          maximumAge: 0,
        },
      );
    });
  }

  private handlePosition(
    lat: number, lng: number, accuracy: number,
    speed: number | null, heading: number | null,
    altitude: number | null, ts: number,
  ) {
    // Accept every fix — no accuracy gating, no speed filtering.
    // RouteProcessor handles smoothing and dedup.
    this.pointCount++;
    const point: GpsPoint = {
      lat,
      lng,
      ts,
      accuracy,
      speed: speed !== null && speed >= 0 ? speed : 0,
      heading: heading ?? 0,
      altitude: altitude !== null && !isNaN(altitude) ? altitude : null,
      moving: true,
    };

    this.diag.status = "acquired";
    this.diag.permission = "granted";
    this.diag.latitude = lat;
    this.diag.longitude = lng;
    this.diag.accuracy = accuracy;
    this.diag.speed = point.speed;
    this.diag.heading = point.heading;
    this.diag.altitude = point.altitude;
    this.diag.lastUpdate = Date.now();
    this.diag.pointCount = this.pointCount;
    this.diag.fixValidated = true;
    this.diag.errorCode = null;
    this.diag.errorMessage = null;
    this.emitDiag();

    if (this.callback) this.callback(point);
  }

  private handleWebError(err: GeolocationPositionError) {
    this.diag.errorCode = err.code;
    this.diag.errorMessage = err.message || "";

    if (err.code === 1) {
      // Permission denied
      this.diag.permission = "denied";
      this.diag.status = "error";
      if (this.errorCallback) this.errorCallback("permission-denied");
    } else if (err.code === 3) {
      // Timeout — keep polling, don't change status if we already have fixes
      if (this.pointCount === 0) {
        this.diag.status = "searching";
      } else {
        this.diag.status = "lost";
      }
    } else {
      // Position unavailable
      this.diag.permission = "granted";
      if (this.pointCount === 0) {
        this.diag.status = "error";
        if (this.errorCallback) this.errorCallback("unavailable");
      } else {
        this.diag.status = "lost";
      }
    }
    this.emitDiag();
    if (this.callback) this.callback(null);
  }

  private handleError(err: unknown) {
    const rawCode = (err as { code?: number | string })?.code ?? -1;
    const message = (err as { message?: string })?.message ?? "Unknown error";
    const codeStr = String(rawCode);

    this.diag.errorMessage = message;

    if (codeStr === "1" || codeStr === "OS-PLUG-GLOC-0003") {
      this.diag.permission = "denied";
      this.diag.status = "error";
      this.diag.errorCode = 1;
      if (this.errorCallback) this.errorCallback("permission-denied");
    } else if (codeStr.includes("OS-PLUG-GLOC-0007") || codeStr.includes("OS-PLUG-GLOC-0017")) {
      this.diag.permission = "granted";
      this.diag.status = "error";
      this.diag.errorCode = 2;
      this.diag.errorMessage = "Location services disabled. Enable GPS in phone settings.";
      if (this.errorCallback) this.errorCallback("unavailable");
    } else if (codeStr === "3" || codeStr.includes("OS-PLUG-GLOC-0010")) {
      this.diag.errorCode = 3;
      if (this.pointCount === 0) {
        this.diag.status = "searching";
      } else {
        this.diag.status = "lost";
      }
    } else {
      this.diag.errorCode = typeof rawCode === "number" ? rawCode : null;
      if (this.pointCount === 0) {
        this.diag.status = "searching";
      } else {
        this.diag.status = "lost";
      }
    }
    this.emitDiag();
    if (this.callback) this.callback(null);
  }

  private emitDiag() {
    if (this.diagCallback) this.diagCallback({ ...this.diag });
  }
}

export function haversine(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371000;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

export async function checkLocationPermission(): Promise<{ granted: boolean; error?: GpsErrorType }> {
  const native = Capacitor.isNativePlatform();

  if (native) {
    try {
      const perm = await Geolocation.checkPermissions();
      if (perm.location === "denied" && perm.coarseLocation === "denied") {
        return { granted: false, error: "permission-denied" };
      }
      return { granted: true };
    } catch {
      return { granted: true };
    }
  }

  if (!navigator.geolocation) return { granted: false, error: "not-supported" };

  if ("permissions" in navigator) {
    try {
      const result = await navigator.permissions.query({ name: "geolocation" as PermissionName });
      if (result.state === "denied") {
        return { granted: false, error: "permission-denied" };
      }
      return { granted: true };
    } catch {
      // Fall through
    }
  }

  return { granted: true };
}
